import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { usePrefs, useLabels, useRequestData, useFlags } from 'wsm-common-data';
import { setClassNames } from 'ddc-classnames-js';
import {
	HighlightedAttribute,
	getFuelTypeIconAndPopoverLabel,
	LocationPopoverContent,
	isValidFuelType,
	isInTransit,
	getInTransitLabel
} from 'wsm-highlighted-attribute';
import { highlightedAttrList } from '../../../global-constants';
import { getAttributeValue } from '../../../utilities/highlightedAttributes';
import { selectIsGridLayout } from '../../../features/layoutSlice';

const FORMATED_NUMBER_COMMA_REGEX = /^\d{1,3}(?:,\d{3})*/;

const HighlightedAttributeContainer = ({ highlightedAttributes }) => {
	const isGridLayout = useSelector((state) => selectIsGridLayout(state));

	const {
		enableLocationHighlight,
		enableFuelTypeHighlight,
		highlightedFuelTypeElectricIcon,
		highlightedFuelTypeHybridIcon
	} = usePrefs();
	const labels = useLabels();
	const { deviceType, locale } = useRequestData();
	const flags = useFlags();
	const isMobile = deviceType === 'MOBILE';

	const useHighlightedAttrbuteIDC =
		flags['use-highlighted-attributes-from-idc'];

	let fuelTypeAttribute;
	const fuelType = getAttributeValue(
		highlightedAttributes,
		highlightedAttrList.FUEL_TYPE
	);
	if (
		(enableFuelTypeHighlight === 'true' || useHighlightedAttrbuteIDC) &&
		isValidFuelType(fuelType)
	) {
		const [fuelTypeIcon, fuelTypePopover] = getFuelTypeIconAndPopoverLabel({
			fuelType,
			electricIcon: highlightedFuelTypeElectricIcon,
			hybridIcon: highlightedFuelTypeHybridIcon,
			labels
		});

		fuelTypeAttribute = (
			<HighlightedAttribute
				icon={fuelTypeIcon}
				popoverContent={fuelTypePopover}
				type="fuelType"
				chipClasses={[
					fuelType.toLowerCase(),
					'mr-3',
					isGridLayout ? 'mb-3' : ''
				]}
			>
				{fuelType}
			</HighlightedAttribute>
		);
	}

	let locationAttribute;
	const account = getAttributeValue(
		highlightedAttributes,
		highlightedAttrList.LOCATION
	);
	if (
		(enableLocationHighlight === 'true' || useHighlightedAttrbuteIDC) &&
		account?.name
	) {
		const cleanTitle = window?._?.unescape(account.name);
		const popoverAria = `${labels.get(
			'THIS_VEHICLE_IS_LOCATED_AT'
		)} ${cleanTitle}`;
		let popoverContent;
		if (Object.keys(account.address).length !== 0 || account.phone) {
			popoverContent = (
				<LocationPopoverContent
					phone={account.phone}
					id={account.id}
					address={account.address}
					isMobile={isMobile}
				/>
			);
		}
		locationAttribute = (
			<HighlightedAttribute
				icon="ddc-icon-map"
				popoverContent={popoverContent}
				type="location"
				chipClasses={['location', 'mr-3', isGridLayout ? 'mb-3' : '']}
				popoverAria={popoverAria}
			>
				{cleanTitle}
			</HighlightedAttribute>
		);
	}

	let inTransitAttribute;
	const inTransitData = getAttributeValue(
		highlightedAttributes,
		highlightedAttrList.ESTIMATED_DELIVERY_DATE
	);

	if (isInTransit(inTransitData?.status)) {
		const { estimatedDeliveryDate } = inTransitData;
		const inTransitText = getInTransitLabel({
			estimatedDeliveryDate,
			locale,
			labels
		});

		inTransitAttribute = (
			<HighlightedAttribute
				popoverContent={inTransitText}
				type="inTransit"
				chipClasses={['mr-3', isGridLayout ? 'mb-3' : '']}
			>
				{inTransitText}
			</HighlightedAttribute>
		);
	}

	let odometerAttribute;
	const odometer = getAttributeValue(
		highlightedAttributes,
		highlightedAttrList.ODOMETER
	);

	/**
	 * When vehicle don't have odometer or it's value is 0, the value is still return with odometer unit 'miles', 'km', etc.
	 * Need use regex to check and only display highlighted odometer have formated odometer value and odometer unit
	 * The value is valid if contains odometer value from 1 to 3 digits or if number have more than 3 digits,
	 * we have comma for each 3 degits group.
	 */
	const isValidOdometer = FORMATED_NUMBER_COMMA_REGEX.test(odometer);

	if (useHighlightedAttrbuteIDC && isValidOdometer) {
		const odometerPopoverContent = getAttributeValue(
			highlightedAttributes,
			highlightedAttrList.ODOMETER,
			true
		);
		odometerAttribute = (
			<HighlightedAttribute
				popoverContent={odometerPopoverContent}
				type="odometer"
				chipClasses={['mr-3', isGridLayout ? 'mb-3' : '']}
			>
				{odometer}
			</HighlightedAttribute>
		);
	}

	const isEnabled = [
		inTransitAttribute,
		locationAttribute,
		fuelTypeAttribute,
		odometerAttribute
	].some((attr) => !!attr);

	if (isEnabled) {
		return (
			<div
				className={setClassNames([
					'vehicle-card-highlight',
					'mb-3',
					'align-self-start',
					'd-flex',
					'flex-wrap'
				])}
			>
				{inTransitAttribute}
				{locationAttribute}
				{fuelTypeAttribute}
				{odometerAttribute}
			</div>
		);
	}
	return null;
};

HighlightedAttributeContainer.propTypes = {
	highlightedAttributes: PropTypes.PropTypes.arrayOf(PropTypes.shape({}))
};

export default HighlightedAttributeContainer;
